/**
 * Server-state conventions for the console (TanStack Query):
 * - Query keys are the API path itself (`['/systems?status=active']`), so invalidating by prefix is trivial.
 * - Mutations toast their own errors unless the caller handles field errors in a form.
 */
import { QueryClient, useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useToast } from '@uatp/ui';
import { ApiError, get } from '../api/client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

type GetOptions<T> = Omit<UseQueryOptions<T, ApiError, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;

/** GET an API path (relative to /api/v1). Pass `null` to disable the query. */
export function useGet<T>(path: string | null, opts: GetOptions<T> = {}) {
  return useQuery<T, ApiError, T, readonly unknown[]>({
    ...opts,
    queryKey: [path ?? '__disabled__'],
    queryFn: ({ signal }) => get<T>(path as string, signal),
    enabled: path !== null && (opts.enabled ?? true),
  });
}

/** Invalidate every cached query whose path starts with one of the prefixes (e.g. '/systems'). */
export function invalidate(...prefixes: string[]): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey[0];
      return typeof key === 'string' && prefixes.some((p) => key.startsWith(p));
    },
  });
}

export interface MutationOptions<TVars, TData> {
  /** Path prefixes to invalidate on success. */
  invalidates?: string[] | ((data: TData, vars: TVars) => string[]);
  /** Success toast. */
  success?: string | ((data: TData, vars: TVars) => string) | null;
  onSuccess?: (data: TData, vars: TVars) => void | Promise<void>;
  onError?: (err: ApiError, vars: TVars) => void;
  /** Suppress the error toast for validation errors that a form renders inline (default true). */
  inlineValidation?: boolean;
}

/** A mutation that invalidates caches, toasts success, and toasts errors (RFC 7807 `detail`). */
export function useApiMutation<TVars = void, TData = unknown>(fn: (vars: TVars) => Promise<TData>, opts: MutationOptions<TVars, TData> = {}) {
  const toast = useToast();
  return useMutation<TData, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: async (data, vars) => {
      const inv = typeof opts.invalidates === 'function' ? opts.invalidates(data, vars) : opts.invalidates;
      if (inv?.length) await invalidate(...inv);
      const msg = typeof opts.success === 'function' ? opts.success(data, vars) : opts.success;
      if (msg) toast.success(msg);
      await opts.onSuccess?.(data, vars);
    },
    onError: (err, vars) => {
      opts.onError?.(err, vars);
      const hasFieldErrors = err instanceof ApiError && Object.keys(err.fieldErrors).length > 0;
      if (hasFieldErrors && (opts.inlineValidation ?? true)) return;
      toast.error(errorMessage(err));
    },
  });
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem?.detail ?? err.problem?.title ?? err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}
